import DOMPurify from 'dompurify';

// Sanitize blog article bodies before injecting them with dangerouslySetInnerHTML.
//
// Blog bodies are authored only by Super Admins, so this is defence in depth
// rather than the sole trust boundary. DOMPurify strips scripts, event handlers,
// and dangerous URLs while keeping the formatting tags an article needs. Links
// are forced to open safely (rel/target added in the hook below).
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A' && node.getAttribute('href')) {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer nofollow');
    }
});

// Matches bare http(s):// / www. URLs and email addresses so we can turn plain
// text into clickable links. Trailing punctuation is intentionally excluded.
const URL_OR_EMAIL = /((?:https?:\/\/|www\.)[^\s<]+[^\s<.,;:!?)\]}'"]|[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi;

// Never linkify text that is already inside a link or code/pre block.
const SKIP_ANCESTORS = new Set(['A', 'CODE', 'PRE', 'SCRIPT', 'STYLE']);

function hrefFor(match: string): string {
    if (match.includes('@') && !/^https?:\/\//i.test(match)) return `mailto:${match}`;
    if (/^www\./i.test(match)) return `https://${match}`;
    return match;
}

// Walk text nodes and replace bare URLs/emails with anchor elements.
function linkifyTextNodes(root: DocumentFragment): void {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const targets: Text[] = [];
    let node = walker.nextNode() as Text | null;
    while (node) {
        const parent = node.parentElement;
        const insideSkipped = (() => {
            let el: Element | null = parent;
            while (el) {
                if (SKIP_ANCESTORS.has(el.tagName)) return true;
                el = el.parentElement;
            }
            return false;
        })();
        // Reset lastIndex — URL_OR_EMAIL is a global regex, so .test() is stateful
        // across calls and would otherwise skip matches in later text nodes.
        URL_OR_EMAIL.lastIndex = 0;
        if (!insideSkipped && node.nodeValue && URL_OR_EMAIL.test(node.nodeValue)) {
            targets.push(node);
        }
        node = walker.nextNode() as Text | null;
    }

    for (const textNode of targets) {
        const text = textNode.nodeValue ?? '';
        URL_OR_EMAIL.lastIndex = 0;
        const frag = document.createDocumentFragment();
        let lastIndex = 0;
        let m: RegExpExecArray | null;
        while ((m = URL_OR_EMAIL.exec(text)) !== null) {
            const [match] = m;
            if (m.index > lastIndex) {
                frag.appendChild(document.createTextNode(text.slice(lastIndex, m.index)));
            }
            const a = document.createElement('a');
            a.setAttribute('href', hrefFor(match));
            a.setAttribute('target', '_blank');
            a.setAttribute('rel', 'noopener noreferrer nofollow');
            a.textContent = match;
            frag.appendChild(a);
            lastIndex = m.index + match.length;
        }
        if (lastIndex < text.length) {
            frag.appendChild(document.createTextNode(text.slice(lastIndex)));
        }
        textNode.parentNode?.replaceChild(frag, textNode);
    }
}

export function sanitizeHtml(dirty: string | null | undefined): string {
    if (!dirty) return '';
    if (typeof window === 'undefined') return '';

    // Sanitize first, then linkify bare URLs in the clean DOM (so a malicious
    // string can't smuggle markup in through the linkify step).
    const clean = DOMPurify.sanitize(dirty, { USE_PROFILES: { html: true } });
    const template = document.createElement('template');
    template.innerHTML = clean;
    linkifyTextNodes(template.content);
    return template.innerHTML;
}
